/**
 * Stats Dual-Write Test Suite
 *
 * This utility verifies that daily stats and product stats are properly synced
 * between Firestore and Supabase after operations.
 *
 * APPROACH: "Fresh Test Store" - Creates test operations (sales/supplies/returns/etc),
 * waits for sync, then compares computed stats in both databases.
 *
 * Why this approach:
 * - Supabase uses COMPUTED VIEWS that aggregate from source tables (sales, supplies)
 * - Firestore uses STORED DOCUMENTS that must be manually updated when operations occur
 * - Historical data may not match, but freshly created operations should trigger proper updates
 *
 * Usage:
 *   import { runStatsDualWriteTests } from './utils/statsDualWriteTest';
 *   await runStatsDualWriteTests(db, options);
 */

import { sanitizeForFirestore } from '../global/Firebase/productSanitize'
import {
  saveNewSale,
  deleteSale,
  saveSaleReturn,
  deleteSaleReturn,
  saveNewSpending,
  deleteSpending,
} from '../global/Firebase/firebase';
import {
  saveNewSupply,
  deleteSupply,
  saveNewWriteOff,
  deleteWriteOff,
} from '../global/Firebase/inventory';
import { shouldUseSupabase } from '../global/Firebase/types';
import { api } from '../global/API/apiClient';
import { getDefaultStore } from 'jotai';
import {
  organizationAtom,
  storesDictAtom,
  productsDictAtom,
  dbAtom,
  userIdAtom,
  constantsAtom,
} from '../global/AtomsProvider';
import { generateProductReportForDate } from './validationFunctions';

// Test result tracking
const testResults = {
  passed: [],
  failed: [],
  skipped: [],
};

/**
 * Comprehensive test variation definitions for stats tests
 * Covers all operations that update daily_stats in the documentation
 */
export const STATS_TEST_VARIATIONS = [
  // ============================================
  // DAILY STATS TESTS (DS - from documentation)
  // ============================================

  // DS1.1: Sale updates daily stats
  {
    id: 'DS1.1',
    name: 'Sale updates daily stats',
    category: 'daily_stats',
    operation: 'sale',
    description: 'Creates a single sale, verifies daily_stats show salesAmount, salesQuantity',
  },

  // DS1.2: Return updates daily stats
  {
    id: 'DS1.2',
    name: 'Return updates daily stats',
    category: 'daily_stats',
    operation: 'return',
    description: 'Creates a sale then return, verifies daily_stats show returnAmount, returnQuantity',
  },

  // DS1.3: Supply updates daily stats
  {
    id: 'DS1.3',
    name: 'Supply updates daily stats',
    category: 'daily_stats',
    operation: 'supply',
    description: 'Creates a supply, verifies daily_stats show suppliesAmount, suppliesQuantity',
  },

  // DS1.4: Writeoff updates daily stats
  {
    id: 'DS1.4',
    name: 'Writeoff updates daily stats',
    category: 'daily_stats',
    operation: 'writeoff',
    description: 'Creates a writeoff, verifies daily_stats show writeoffsAmount, writeoffsQuantity',
  },

  // DS1.5: Spending updates daily stats
  {
    id: 'DS1.5',
    name: 'Spending updates daily stats',
    category: 'daily_stats',
    operation: 'spending',
    description: 'Creates a spending, verifies daily_stats show spendingsAmount',
  },

  // DS3.1: Multiple operations same day
  {
    id: 'DS3.1',
    name: 'Multiple operations - aggregation',
    category: 'daily_stats',
    operation: 'multi_operation',
    description: 'Creates sale + supply, verifies all aggregates in daily_stats',
  },

  // ============================================
  // PRODUCT STATS TESTS (PR - Product Reports)
  // ============================================

  // PR1.1: Sales by product
  {
    id: 'PR1.1',
    name: 'Sale - verify product stats (sold)',
    category: 'product_stats',
    operation: 'sale',
    description: 'Creates a sale, verifies product_stats show soldQuantity, soldAmount',
  },

  // PR2.1: Supplies by product
  {
    id: 'PR2.1',
    name: 'Supply - verify product stats (supplied)',
    category: 'product_stats',
    operation: 'supply',
    description: 'Creates a supply, verifies product_stats show suppliedQuantity, suppliedAmount',
  },

  // PR3.2: Stock movements history (writeoff)
  {
    id: 'PR3.2',
    name: 'Writeoff - verify product stats (writtenOff)',
    category: 'product_stats',
    operation: 'writeoff',
    description: 'Creates a writeoff, verifies product_stats show writeoffQuantity',
  },

  // PR1.2: Returns by product
  {
    id: 'PR1.2',
    name: 'Return - verify product stats (returned)',
    category: 'product_stats',
    operation: 'return',
    description: 'Creates a sale then return, verifies product_stats show returnedQuantity',
  },
];

// Legacy test variations for comparing existing data (kept for backward compatibility)
export const DAILY_STATS_TEST_VARIATIONS = [
  { id: 'DS_LEGACY_1', name: 'Compare today daily stats', category: 'daily_stats', daysBack: 0 },
  { id: 'DS_LEGACY_2', name: 'Compare yesterday daily stats', category: 'daily_stats', daysBack: 1 },
];

export const PRODUCT_STATS_TEST_VARIATIONS = [
  { id: 'PS_LEGACY_1', name: 'Compare product stats for first product today', category: 'product_stats', daysBack: 0, productIndex: 0 },
];

/**
 * Format date as YYYY-MM-DD
 */
function formatDate(date) {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format date for dailyStatID (d-M-y format)
 */
function formatDateForDailyStat(date) {
  const d = date.getDate();
  const m = date.getMonth() + 1;
  const y = date.getFullYear();
  return `${d}-${m}-${y}`;
}

/**
 * Compare daily stats for a specific store and date
 */
async function compareDailyStats(storeId, date, options = {}) {
  const { logSessionId } = options;

  try {
    const url = `/reports/compare/daily-stats?storeId=${encodeURIComponent(storeId)}&date=${encodeURIComponent(date)}${logSessionId ? `&logSessionId=${logSessionId}` : ''}`;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Compare daily stats failed:', error);
    throw error;
  }
}

/**
 * Compare product stats for a specific store, product, and date
 */
async function compareProductStats(storeId, productId, date, options = {}) {
  const { logSessionId } = options;

  try {
    const url = `/reports/compare/product-stats?storeId=${encodeURIComponent(storeId)}&productId=${encodeURIComponent(productId)}&date=${encodeURIComponent(date)}${logSessionId ? `&logSessionId=${logSessionId}` : ''}`;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Compare product stats failed:', error);
    throw error;
  }
}

/**
 * Create a test sale for stats testing
 */
function createTestSaleData(testData) {
  const { organizationId, storeId, products, paymentMethods, purchaseMethods } = testData;
  const now = new Date();
  const product = products[0];

  // Get sale price for this store
  const salePrice = product.salePrices?.[storeId] || product.price || 1000;
  const purchasePrice = product.purchasePrice || 500;
  const quantity = 1;

  // Default purchase method
  const purchaseMethod = purchaseMethods?.find(p => p.id === 'shop-offline' || p.firestoreId === 'shop-offline') || {
    id: 'shop-offline',
    firestoreId: 'shop-offline',
    name: 'Shop',
    icon: 'house-user',
    iconText: 'Shop',
  };

  return {
    organizationId,
    store: storeId,
    saleDate: now,
    // Use standard dailyStatID format (d-M-y-storeId) to ensure stats are written
    // to the same document that the comparison endpoint queries
    dailyStatID: `${formatDateForDailyStat(now)}-${storeId}`,
    cashierUID: 'stats-test-cashier',
    cartItems: [{
      id: product.id,
      name: product.name || 'Test Product',
      quantity,
      price: salePrice,
      salePrices: product.salePrices || { [storeId]: salePrice },
      finalItemPrice: salePrice * quantity,
      itemPriceWithoutModifiers: salePrice * quantity,
      purchasePrice,
      punchCardEnabled: false,
      ref: product.ref,
    }],
    totalCartPrice: salePrice * quantity,
    finalTotalCartPrice: salePrice * quantity,
    totalPurchasePrice: purchasePrice * quantity,
    purchaseMethod,
    paymentMethod: paymentMethods[0],
    clientId: null,
    client: null,
    discountAmount: 0,
    discountPercentage: 0,
    punchCardEnabled: false,
    punchCardCapacity: null,
    punchCardReduction: 0,
    cashbackEnabled: false,
    bonusAccumulation: 0,
    bonusReduction: 0,
    bonusAction: null,
    comment: 'Stats dual-write test sale',
    tableNumber: null,
    saleType: 'product',
  };
}

/**
 * Create a test supply for stats testing
 */
function createTestSupplyData(testData) {
  const { organizationId, storeId, products } = testData;
  const now = new Date();
  const product = products[0];

  const purchasePrice = product.purchasePrice || 500;
  const quantity = 5;

  return {
    organizationId,
    store: storeId,
    supplyDate: now,
    // Use standard dailyStatID format (d-M-y-storeId) to ensure stats are written
    // to the same document that the comparison endpoint queries
    dailyStatID: `${formatDateForDailyStat(now)}-${storeId}`,
    cashierUID: 'stats-test-cashier',
    items: [{
      id: product.id,
      name: product.name || 'Test Product',
      ref: product.ref,
      quantity,
      returnQuantity: 0,
      purchasePrice,
      price: purchasePrice,
      totalPrice: purchasePrice * quantity,
      product: {
        name: product.name,
        purchasePrice,
      },
    }],
    totalSum: purchasePrice * quantity,
    totalPrice: purchasePrice * quantity,
    isPaid: false,
    supplierID: null,
    supplierName: null,
    comment: 'Stats dual-write test supply',
  };
}

/**
 * Create test writeoff data for stats testing
 */
function createTestWriteoffData(testData) {
  const { organizationId, storeId, products } = testData;
  const now = new Date();
  const product = products[0];

  const purchasePrice = product.purchasePrice || 500;
  const quantity = 2;

  return {
    organizationId,
    store: storeId,
    date: now,
    dailyStatID: `${formatDateForDailyStat(now)}-${storeId}`,
    cashierUID: 'stats-test-cashier',
    product: {
      id: product.id,
      name: product.name || 'Test Product',
      purchasePrice,
      ref: product.ref,
      isComposite: false,
    },
    quantity,
    purchasePrice,
    totalAmount: purchasePrice * quantity,
    reason: 'Stats dual-write test',
    comment: 'Stats dual-write test writeoff',
  };
}

/**
 * Create test spending data for stats testing
 */
function createTestSpendingData(testData) {
  const { organizationId, storeId, store } = testData;
  const now = new Date();

  // Get default wallet
  const wallets = store?.wallets || [];
  const defaultWallet = wallets.find(w => w.isCash) || wallets[0] || { id: 'default-wallet', name: 'Cash' };

  return {
    organizationId,
    store: storeId,
    date: now,
    dailyStatID: `${formatDateForDailyStat(now)}-${storeId}`,
    cashierUID: 'stats-test-cashier',
    amount: 1500,
    category: 'Test Category',
    name: 'Stats test spending',
    comment: 'Stats dual-write test spending',
    source: defaultWallet,
  };
}

/**
 * Create return info for a sale
 */
function createReturnInfo(saleData, testData) {
  const now = new Date();
  const { storeId } = testData;

  const cartItem = saleData.cartItems[0];
  const returnQuantity = 1;
  const returnAmount = cartItem.price * returnQuantity;

  // IMPORTANT: Use 'returnMethod' not 'refundedPaymentMethod'
  // saveSaleReturn and dailyStatIncrementsForReturns both use returnInfo.returnMethod
  // to get the wallet ID via returnMethod.destinationWalletForStore[store].id
  return {
    returnType: 1, // 1 = money refund
    returnDate: now,
    date: now, // Also set 'date' field which is used by some functions
    dailyStatID: `${formatDateForDailyStat(now)}-${storeId}`,
    updateStock: true,
    returnAmount,
    totalReturnedAmount: returnAmount, // Required by dailyStatIncrementsForReturns
    returnMethod: saleData.paymentMethod, // Use returnMethod, not refundedPaymentMethod
    items: [{
      ...cartItem,
      returnedQuantity: returnQuantity,
      returnedAmount: returnAmount,
      isReturned: true,
    }],
  };
}

/**
 * Run a single stats test variation
 */
async function runStatsTest(db, variation, testData, options = {}) {
  const { delayMs = 6000, logSessionId } = options;
  const testId = variation.id;
  const startTime = Date.now();

  console.log(`\n=== Running test ${testId}: ${variation.name} ===`);
  console.log(`Description: ${variation.description}`);

  const createdDocuments = [];
  let testProductId = null;

  try {
    // Step 1: Create test operation(s) based on variation type
    if (variation.operation === 'sale') {
      const saleData = createTestSaleData(testData);
      testProductId = saleData.cartItems[0].id;
      console.log(`Creating test sale for product: ${saleData.cartItems[0].name}`);

      const saleRef = await saveNewSale(db, saleData);
      createdDocuments.push({ type: 'sale', ref: saleRef, data: saleData });
      console.log(`Created sale: ${saleRef.id}`);

    } else if (variation.operation === 'supply') {
      const supplyData = createTestSupplyData(testData);
      testProductId = supplyData.items[0].id;
      console.log(`Creating test supply for product: ${supplyData.items[0].name}`);

      const { ref: supplyRef } = await saveNewSupply(db, supplyData);
      createdDocuments.push({ type: 'supply', ref: supplyRef, data: supplyData });
      console.log(`Created supply: ${supplyRef.id}`);

    } else if (variation.operation === 'return') {
      // First create a sale, then create a return on it
      const saleData = createTestSaleData(testData);
      testProductId = saleData.cartItems[0].id;
      console.log(`Creating test sale (for return) for product: ${saleData.cartItems[0].name}`);

      const saleRef = await saveNewSale(db, saleData);
      createdDocuments.push({ type: 'sale', ref: saleRef, data: saleData });
      console.log(`Created sale: ${saleRef.id}`);

      // Wait briefly for sale to be processed
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Create return info
      const returnInfo = createReturnInfo(saleData, testData);
      const saleWithReturn = {
        ...saleData,
        returnInfo,
        ref: saleRef,
      };

      console.log(`Creating return on sale: ${saleRef.id}`);
      await saveSaleReturn(db, saleRef, saleWithReturn, saleRef.id, saleData.cashierUID);
      createdDocuments.push({
        type: 'return',
        saleRef,
        saleData: saleWithReturn,
        returnInfo,
      });
      console.log(`Created return on sale: ${saleRef.id}`);

    } else if (variation.operation === 'writeoff') {
      const writeoffData = createTestWriteoffData(testData);
      testProductId = writeoffData.product.id;
      console.log(`Creating test writeoff for product: ${writeoffData.product.name}`);

      const writeoffRef = await saveNewWriteOff(db, writeoffData);
      createdDocuments.push({ type: 'writeoff', ref: writeoffRef, data: writeoffData });
      console.log(`Created writeoff: ${writeoffRef.id}`);

    } else if (variation.operation === 'spending') {
      const spendingData = createTestSpendingData(testData);
      console.log(`Creating test spending: ${spendingData.name}`);

      const { ref: spendingRef } = await saveNewSpending(db, spendingData);
      createdDocuments.push({ type: 'spending', ref: spendingRef, data: spendingData });
      console.log(`Created spending: ${spendingRef.id}`);

    } else if (variation.operation === 'multi_operation') {
      // Create multiple operations to test aggregation
      const saleData = createTestSaleData(testData);
      testProductId = saleData.cartItems[0].id;
      console.log(`Creating test sale for multi-op test`);
      const saleRef = await saveNewSale(db, saleData);
      createdDocuments.push({ type: 'sale', ref: saleRef, data: saleData });

      const supplyData = createTestSupplyData(testData);
      console.log(`Creating test supply for multi-op test`);
      const { ref: supplyRef } = await saveNewSupply(db, supplyData);
      createdDocuments.push({ type: 'supply', ref: supplyRef, data: supplyData });

      console.log(`Created sale: ${saleRef.id}, supply: ${supplyRef.id}`);
    }

    // Step 2: Wait for Supabase sync
    console.log(`Waiting ${delayMs}ms for Supabase sync...`);
    await new Promise(resolve => setTimeout(resolve, delayMs));

    // Step 3: Compare stats
    const today = formatDate(new Date());
    let comparison;

    if (variation.category === 'daily_stats') {
      comparison = await compareDailyStats(testData.storeId, today, { logSessionId });
      console.log('Daily stats comparison result:', JSON.stringify(sanitizeForFirestore(comparison), null, 2));

    } else if (variation.category === 'product_stats') {
      if (!testProductId) {
        throw new Error('No product ID available for product stats test');
      }

      // Check if Supabase is enabled for this organization
      // generateProductReportForDate needs to query sales/supplies/writeoffs.
      // If Supabase is disabled, it falls back to Firestore queries that may need composite indexes.
      // With Supabase enabled, it uses API calls which don't require Firestore indexes.
      if (!shouldUseSupabase()) {
        console.log(`⚠️ Test ${testId} SKIPPED - Supabase not enabled for organization`);
        console.log('Product stats tests require useSupabaseForData=true to avoid Firestore index issues');
        testResults.skipped.push({
          variation,
          reason: 'Supabase not enabled for organization (useSupabaseForData=false)',
          date: today,
        });
        return { testId, status: 'skipped', reason: 'Supabase not enabled' };
      }

      // IMPORTANT: Generate Firestore product stats before comparison
      // Firestore product stats are NOT incrementally updated - they are generated on-demand
      // by calling generateProductReportForDate. Without this, Firestore would have no product stats.
      console.log('Generating Firestore product stats for comparison...');
      await generateProductReportForDate(new Date(), testData.storeId);
      console.log('Product stats generated successfully');

      comparison = await compareProductStats(testData.storeId, testProductId, today, { logSessionId });
      console.log('Product stats comparison result:', JSON.stringify(sanitizeForFirestore(comparison), null, 2));
    }

    // Step 4: Evaluate results
    if (!comparison.foundInSupabase && !comparison.foundInFirestore) {
      console.log(`⚠️ Test ${testId} SKIPPED - No stats found in either database after creating operations`);
      testResults.skipped.push({
        variation,
        reason: 'No stats found after operations',
        date: today,
      });
      return { testId, status: 'skipped', reason: 'No stats found after operations' };
    }

    if (comparison.matches) {
      console.log(`✅ Test ${testId} PASSED - Stats match between Firestore and Supabase`);
      testResults.passed.push({
        variation,
        duration: Date.now() - startTime,
        date: today,
        result: comparison,
      });
      return { testId, status: 'passed', comparison };

    } else {
      console.log(`❌ Test ${testId} FAILED - Stats mismatch`);
      console.log('Differences:', JSON.stringify(sanitizeForFirestore(comparison.differences), null, 2));
      testResults.failed.push({
        variation,
        duration: Date.now() - startTime,
        date: today,
        result: comparison,
      });
      return { testId, status: 'failed', comparison };
    }

  } catch (error) {
    console.error(`❌ Test ${testId} ERROR:`, error.message);
    testResults.failed.push({
      variation,
      duration: Date.now() - startTime,
      error: error.message,
    });
    return { testId, status: 'error', error: error.message };

  } finally {
    // Step 5: Cleanup - delete created documents
    console.log(`Cleaning up ${createdDocuments.length} test document(s)...`);
    for (const doc of createdDocuments) {
      try {
        if (doc.type === 'sale') {
          const saleDoc = await doc.ref.get();
          if (saleDoc.exists) {
            const firestoreData = saleDoc.data();
            const cleanupData = {
              ...doc.data,
              ref: doc.ref,
              transactionId: firestoreData.transactionId,
              cartItems: doc.data.cartItems.map((item, idx) => ({
                ...item,
                cartItemRef: firestoreData.cartItems?.[idx]?.cartItemRef || item.cartItemRef,
              })),
            };
            await deleteSale(db, doc.ref, cleanupData, testData.uid);
            console.log(`Cleaned up sale: ${doc.ref.id}`);
          }
        } else if (doc.type === 'supply') {
          const supplyDoc = await doc.ref.get();
          if (supplyDoc.exists) {
            const firestoreData = supplyDoc.data();
            const cleanupData = {
              ...doc.data,
              ref: doc.ref,
              transactionId: firestoreData.transactionId,
            };
            await deleteSupply(db, doc.ref, cleanupData);
            console.log(`Cleaned up supply: ${doc.ref.id}`);
          }
        } else if (doc.type === 'return') {
          // Get the return document
          const returnsQuery = await db.collection('returns').where('saleID', '==', doc.saleRef.id).get();
          if (!returnsQuery.empty) {
            const returnDoc = returnsQuery.docs[0];
            const saleDoc = await doc.saleRef.get();
            if (saleDoc.exists) {
              await deleteSaleReturn(db, returnDoc.ref, doc.saleRef, saleDoc.data(), doc.returnInfo);
              console.log(`Cleaned up return for sale: ${doc.saleRef.id}`);
            }
          }
          // Also clean up the underlying sale
          const saleDoc = await doc.saleRef.get();
          if (saleDoc.exists) {
            const firestoreData = saleDoc.data();
            const cleanupData = {
              ...doc.saleData,
              ref: doc.saleRef,
              transactionId: firestoreData.transactionId,
            };
            await deleteSale(db, doc.saleRef, cleanupData, testData.uid);
            console.log(`Cleaned up sale (after return): ${doc.saleRef.id}`);
          }
        } else if (doc.type === 'writeoff') {
          const writeoffDoc = await doc.ref.get();
          if (writeoffDoc.exists) {
            await deleteWriteOff(db, doc.ref, doc.data);
            console.log(`Cleaned up writeoff: ${doc.ref.id}`);
          }
        } else if (doc.type === 'spending') {
          const spendingDoc = await doc.ref.get();
          if (spendingDoc.exists) {
            const firestoreData = spendingDoc.data();
            await deleteSpending(db, doc.ref, { ...doc.data, transactionId: firestoreData.transactionId });
            console.log(`Cleaned up spending: ${doc.ref.id}`);
          }
        }
      } catch (cleanupError) {
        console.warn(`Failed to cleanup ${doc.type}:`, cleanupError.message);
        // Try direct delete as fallback
        try {
          if (doc.ref) {
            await doc.ref.delete();
            console.log(`Fallback cleanup: deleted ${doc.type} ${doc.ref.id}`);
          }
        } catch (e) {
          console.warn(`Fallback cleanup also failed: ${e.message}`);
        }
      }
    }

    // Wait for cleanup operations to sync
    await new Promise(resolve => setTimeout(resolve, 2000));
  }
}

/**
 * Run all stats dual-write tests (new approach: create operations, then compare)
 */
export async function runStatsDualWriteTests(passedDb, options = {}) {
  const {
    variations = STATS_TEST_VARIATIONS,
    delayMs = 6000,
    logSessionId,
    stopOnFailure = false,
  } = options;

  console.log('\n========================================');
  console.log('  STATS DUAL-WRITE TEST SUITE');
  console.log('  (Comprehensive Tests)');
  console.log('========================================\n');

  // Reset results
  testResults.passed = [];
  testResults.failed = [];
  testResults.skipped = [];

  // Get test data from Jotai store
  const jotaiStore = getDefaultStore();
  const organization = jotaiStore.get(organizationAtom);
  const storesDict = jotaiStore.get(storesDictAtom);
  const productsDict = jotaiStore.get(productsDictAtom);
  const userId = jotaiStore.get(userIdAtom);

  // Get db from atom if not passed
  const db = passedDb || jotaiStore.get(dbAtom);

  if (!db) {
    throw new Error('Database not initialized. Please wait for app to fully load.');
  }

  if (!organization || Object.keys(organization).length === 0) {
    throw new Error('Organization not found. Please ensure you are logged in.');
  }

  if (!storesDict || Object.keys(storesDict).length === 0) {
    throw new Error('Stores not loaded. Please wait for app to fully load.');
  }

  // Get first store
  const storeId = Object.keys(storesDict)[0];
  if (!storeId) {
    throw new Error('No stores found. Please create a store first.');
  }
  const store = storesDict[storeId];

  // Get products
  const products = Object.values(productsDict || {}).slice(0, 5);
  if (products.length === 0) {
    throw new Error('No products found. Please create products first.');
  }

  // Add refs to products if missing
  products.forEach(p => {
    if (!p.ref && p.id) {
      p.ref = db.collection('products').doc(p.id);
    }
  });

  // Get payment methods from constants atom
  const constants = jotaiStore.get(constantsAtom);
  const allPaymentMethods = constants?.paymentMethods?.data || [];

  // Filter payment methods that have destinationWalletForStore for THIS store specifically
  // This is important for returns - saveSaleReturn looks up wallet by returnMethod.destinationWalletForStore[storeId].id
  let paymentMethods = allPaymentMethods.filter(p =>
    p.destinationWalletForStore &&
    p.destinationWalletForStore[storeId] &&
    p.destinationWalletForStore[storeId].id
  );

  // If no payment methods have wallet for this store, add the mapping
  if (paymentMethods.length === 0) {
    const storeWallets = store?.wallets || organization.wallets || [];
    const defaultWallet = storeWallets[0] || { id: 'default-wallet', name: 'Default' };

    // Ensure wallet has proper structure with id
    const walletForStore = {
      id: defaultWallet.id || defaultWallet.firestoreId || 'default-wallet',
      name: defaultWallet.name || 'Default Wallet',
    };

    if (allPaymentMethods.length > 0) {
      allPaymentMethods.forEach(p => {
        paymentMethods.push({
          ...p,
          destinationWalletForStore: {
            ...(p.destinationWalletForStore || {}),
            [storeId]: walletForStore,
          },
        });
      });
    } else {
      paymentMethods.push({
        id: 'cash',
        name: 'Cash',
        isCash: true,
        destinationWalletForStore: { [storeId]: walletForStore },
      });
    }
  }

  // Get purchase methods
  const purchaseMethods = organization.purchaseMethods || [
    { id: 'shop-offline', name: 'Shop' },
  ];

  const testData = {
    organizationId: organization.id,
    storeId,
    store,
    products,
    paymentMethods,
    purchaseMethods,
    uid: userId || 'stats-test-uid',
  };

  console.log(`Organization: ${organization.name || organization.id}`);
  console.log(`Store: ${store?.name || storeId}`);
  console.log(`Store ID: ${storeId}`);
  console.log(`Products: ${products.length}`);
  console.log(`Payment methods: ${paymentMethods.length}`);
  console.log(`Variations to test: ${variations.length}`);
  console.log('========================================\n');

  // Run each test
  for (const variation of variations) {
    const result = await runStatsTest(db, variation, testData, { delayMs, logSessionId });

    if (stopOnFailure && result?.status === 'failed') {
      console.log('\n⛔ Stopping due to failure (stopOnFailure=true)');
      break;
    }
  }

  // Summary
  console.log('\n========================================');
  console.log('  TEST SUMMARY');
  console.log('========================================');
  console.log(`✅ Passed: ${testResults.passed.length}`);
  console.log(`❌ Failed: ${testResults.failed.length}`);
  console.log(`⚠️ Skipped: ${testResults.skipped.length}`);

  if (testResults.failed.length > 0) {
    console.log('\nFailed tests:');
    testResults.failed.forEach((t) => {
      console.log(`  - ${t.variation?.id || t.testId}: ${t.variation?.name || t.name}`);
      if (t.error) console.log(`    Error: ${t.error}`);
      if (t.result?.differences) {
        t.result.differences.forEach((d) => {
          console.log(`    ${d.field}: Supabase=${d.supabaseValue}, Firestore=${d.firestoreValue}`);
        });
      }
    });
  }

  return {
    passed: testResults.passed,
    failed: testResults.failed,
    skipped: testResults.skipped,
    total: variations.length,
    successRate:
      testResults.passed.length /
      (testResults.passed.length + testResults.failed.length || 1) *
      100,
  };
}

/**
 * Run quick smoke test - just daily stats from a single sale
 */
export async function runQuickStatsSmokeTest(db, options = {}) {
  const { logSessionId } = options;

  console.log('\n========================================');
  console.log('  STATS QUICK SMOKE TEST');
  console.log('========================================\n');

  // Quick test: just daily stats from a single sale
  const quickVariations = [
    STATS_TEST_VARIATIONS.find(v => v.id === 'DS1.1'), // Single sale - daily stats
  ].filter(Boolean);

  return runStatsDualWriteTests(db, {
    variations: quickVariations,
    delayMs: 6000,
    logSessionId,
  });
}

/**
 * Run daily stats tests only
 */
export async function runDailyStatsTests(db, options = {}) {
  const { logSessionId } = options;

  console.log('\n========================================');
  console.log('  DAILY STATS TESTS');
  console.log('========================================\n');

  const dailyStatsVariations = STATS_TEST_VARIATIONS.filter(v => v.category === 'daily_stats');

  return runStatsDualWriteTests(db, {
    variations: dailyStatsVariations,
    delayMs: 6000,
    logSessionId,
  });
}

/**
 * Run product stats tests only
 */
export async function runProductStatsTests(db, options = {}) {
  const { logSessionId } = options;

  console.log('\n========================================');
  console.log('  PRODUCT STATS TESTS');
  console.log('========================================\n');

  const productStatsVariations = STATS_TEST_VARIATIONS.filter(v => v.category === 'product_stats');

  return runStatsDualWriteTests(db, {
    variations: productStatsVariations,
    delayMs: 6000,
    logSessionId,
  });
}

/**
 * Run legacy comparison tests (compares existing data, may fail due to historical sync issues)
 */
export async function runLegacyStatsTests(db, options = {}) {
  const {
    dailyStatsVariations = DAILY_STATS_TEST_VARIATIONS,
    productStatsVariations = PRODUCT_STATS_TEST_VARIATIONS,
    logSessionId,
  } = options;

  console.log('\n========================================');
  console.log('  LEGACY STATS COMPARISON TEST');
  console.log('  (Compares existing historical data)');
  console.log('========================================\n');

  // Reset results
  testResults.passed = [];
  testResults.failed = [];
  testResults.skipped = [];

  const jotaiStore = getDefaultStore();
  const organization = jotaiStore.get(organizationAtom);
  const storesDict = jotaiStore.get(storesDictAtom);
  const productsDict = jotaiStore.get(productsDictAtom);

  if (!organization) {
    throw new Error('No organization found. Please log in first.');
  }

  // Get first store for testing
  const storeIds = Object.keys(storesDict || {});
  if (storeIds.length === 0) {
    throw new Error('No stores found. Please create a store first.');
  }
  const storeId = storeIds[0];
  const store = storesDict[storeId];

  console.log(`Testing with organization: ${organization.name} (${organization.id})`);
  console.log(`Testing with store: ${store?.name || storeId}`);

  // Run daily stats tests
  console.log('\n--- Daily Stats Tests ---');
  for (const variation of dailyStatsVariations) {
    await runLegacyDailyStatsTest(variation, storeId, { logSessionId });
  }

  // Run product stats tests
  console.log('\n--- Product Stats Tests ---');
  for (const variation of productStatsVariations) {
    await runLegacyProductStatsTest(variation, storeId, productsDict, { logSessionId });
  }

  // Summary
  console.log('\n========================================');
  console.log('  TEST SUMMARY (Legacy)');
  console.log('========================================');
  console.log(`✅ Passed: ${testResults.passed.length}`);
  console.log(`❌ Failed: ${testResults.failed.length}`);
  console.log(`⚠️ Skipped: ${testResults.skipped.length}`);

  return {
    passed: testResults.passed,
    failed: testResults.failed,
    skipped: testResults.skipped,
    total: dailyStatsVariations.length + productStatsVariations.length,
    successRate:
      testResults.passed.length /
      (testResults.passed.length + testResults.failed.length || 1) *
      100,
  };
}

/**
 * Run a legacy daily stats test (compares existing data)
 */
async function runLegacyDailyStatsTest(variation, storeId, options = {}) {
  const { logSessionId } = options;
  const testId = variation.id;
  const startTime = Date.now();

  const date = getDateDaysAgo(variation.daysBack);
  console.log(`\n=== Running test ${testId}: ${variation.name} ===`);
  console.log(`Store: ${storeId}, Date: ${date}`);

  try {
    const comparison = await compareDailyStats(storeId, date, { logSessionId });

    if (!comparison.foundInSupabase && !comparison.foundInFirestore) {
      console.log(`⚠️ Test ${testId} SKIPPED - No stats found for ${date}`);
      testResults.skipped.push({
        testId,
        name: variation.name,
        reason: 'No stats found',
        date,
      });
      return { testId, status: 'skipped' };
    }

    if (comparison.matches) {
      console.log(`✅ Test ${testId} PASSED`);
      testResults.passed.push({
        testId,
        name: variation.name,
        duration: Date.now() - startTime,
        date,
      });
      return { testId, status: 'passed', comparison };
    } else {
      console.log(`❌ Test ${testId} FAILED`);
      console.log('Differences:', JSON.stringify(sanitizeForFirestore(comparison.differences), null, 2));
      testResults.failed.push({
        testId,
        name: variation.name,
        duration: Date.now() - startTime,
        date,
        differences: comparison.differences,
      });
      return { testId, status: 'failed', comparison };
    }
  } catch (error) {
    console.error(`❌ Test ${testId} ERROR:`, error.message);
    testResults.failed.push({
      testId,
      name: variation.name,
      error: error.message,
    });
    return { testId, status: 'error', error: error.message };
  }
}

/**
 * Run a legacy product stats test (compares existing data)
 */
async function runLegacyProductStatsTest(variation, storeId, products, options = {}) {
  const { logSessionId } = options;
  const testId = variation.id;
  const startTime = Date.now();

  const date = getDateDaysAgo(variation.daysBack);
  const productIndex = variation.productIndex || 0;

  const productIds = Object.keys(products || {});
  if (productIds.length <= productIndex) {
    console.log(`⚠️ Test ${testId} SKIPPED - Not enough products`);
    testResults.skipped.push({
      testId,
      name: variation.name,
      reason: 'Not enough products',
    });
    return { testId, status: 'skipped' };
  }

  const productId = productIds[productIndex];
  const product = products[productId];
  console.log(`\n=== Running test ${testId}: ${variation.name} ===`);
  console.log(`Store: ${storeId}, Product: ${product?.name || productId}, Date: ${date}`);

  try {
    const comparison = await compareProductStats(storeId, productId, date, { logSessionId });

    if (!comparison.foundInSupabase && !comparison.foundInFirestore) {
      console.log(`⚠️ Test ${testId} SKIPPED - No product stats found`);
      testResults.skipped.push({
        testId,
        name: variation.name,
        reason: 'No product stats found',
        date,
        productId,
      });
      return { testId, status: 'skipped' };
    }

    if (comparison.matches) {
      console.log(`✅ Test ${testId} PASSED`);
      testResults.passed.push({
        testId,
        name: variation.name,
        duration: Date.now() - startTime,
        date,
        productId,
      });
      return { testId, status: 'passed', comparison };
    } else {
      console.log(`❌ Test ${testId} FAILED`);
      console.log('Differences:', JSON.stringify(sanitizeForFirestore(comparison.differences), null, 2));
      testResults.failed.push({
        testId,
        name: variation.name,
        duration: Date.now() - startTime,
        date,
        productId,
        differences: comparison.differences,
      });
      return { testId, status: 'failed', comparison };
    }
  } catch (error) {
    console.error(`❌ Test ${testId} ERROR:`, error.message);
    testResults.failed.push({
      testId,
      name: variation.name,
      error: error.message,
    });
    return { testId, status: 'error', error: error.message };
  }
}

/**
 * Get date N days ago as YYYY-MM-DD
 */
function getDateDaysAgo(daysBack) {
  const date = new Date();
  date.setDate(date.getDate() - daysBack);
  return formatDate(date);
}

/**
 * Compare current stats state (useful for manual verification)
 */
export async function compareCurrentStats(options = {}) {
  const { logSessionId, daysBack = 0 } = options;

  const jotaiStore = getDefaultStore();
  const storesDict = jotaiStore.get(storesDictAtom);
  const productsDict = jotaiStore.get(productsDictAtom);

  const storeIds = Object.keys(storesDict || {});
  if (storeIds.length === 0) {
    throw new Error('No stores found');
  }

  const storeId = storeIds[0];
  const date = getDateDaysAgo(daysBack);

  console.log(`Comparing stats for store ${storeId} on ${date}`);

  // Compare daily stats
  const dailyComparison = await compareDailyStats(storeId, date, { logSessionId });
  console.log('\nDaily Stats Comparison:');
  console.log(JSON.stringify(sanitizeForFirestore(dailyComparison), null, 2));

  // Compare first product stats if products exist
  const productIds = Object.keys(productsDict || {});
  if (productIds.length > 0) {
    const productComparison = await compareProductStats(storeId, productIds[0], date, {
      logSessionId,
    });
    console.log('\nProduct Stats Comparison:');
    console.log(JSON.stringify(sanitizeForFirestore(productComparison), null, 2));
  }

  return {
    dailyStats: dailyComparison,
    storeId,
    date,
  };
}

// Export test variations
export { DAILY_STATS_TEST_VARIATIONS as DAILY_TEST_VARIATIONS };
export { PRODUCT_STATS_TEST_VARIATIONS as PRODUCT_TEST_VARIATIONS };
